import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getContents, createContent } from '../../api/cmsContents';
import ContentCategoryManager from '../../components/admin/ContentCategoryManager';
import AuthorManager from '../../components/admin/AuthorManager';

/**
 * 콘텐츠 검수 — 이관된 초안을 발행 가능한 상태로 만드는 화면.
 *
 * 이관(02 §6)은 45건을 전부 `draft` 로 만들었다. 이관이 "발행된 콘텐츠를 옮기는 일"이
 * 아니라 "초안을 만들고 사람이 발행 조건을 채우는 일"이기 때문이다.
 *
 * 목록에 **남은 조건 건수를 같이 보여 준다.** 이게 없으면 45건을 하나씩 열어 봐야
 * 어느 것부터 손대야 할지 알 수 있다.
 */
const TYPE_LABEL = { note: '기술노트', solution: '솔루션', product: '제품', case: '사례' };
const STATUS_LABEL = { draft: '초안', published: '발행됨', scheduled: '예약', archived: '보관' };

const ContentReview = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [type, setType] = useState('');
    const [status, setStatus] = useState('');
    const [q, setQ] = useState('');
    // 기술노트는 카테고리와 저자가 있어야 발행된다. 둘 다 여기서 바로 연다 —
    // 글을 쓰다 말고 다른 화면을 찾아가게 하면 흐름이 끊긴다.
    const [catOpen, setCatOpen] = useState(false);
    const [authorOpen, setAuthorOpen] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setRows(await getContents({ type, status, q }));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [type, status, q]);

    useEffect(() => { load(); }, [load]);

    const newNote = async () => {
        const title = window.prompt('기술노트 제목을 입력하세요.');
        if (!title || !title.trim()) return;
        setCreating(true); setError('');
        try {
            const c = await createContent({ type: 'note', title: title.trim() });
            navigate(`/admin/contents/${c.id}`);
        } catch (e) {
            setError(e.message);
        } finally {
            setCreating(false);
        }
    };

    const total = rows.length;
    const ready = rows.filter((r) => r.fail === 0).length;
    const published = rows.filter((r) => r.status === 'published').length;

    const selectCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500';

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">콘텐츠 검수</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        발행 조건을 채운 뒤 발행합니다. 조건은 서버가 판정하며, 화면은 그 결과를 보여 줍니다.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button" onClick={newNote} disabled={creating}
                        className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-40"
                    >+ 기술노트 작성</button>
                    <button
                        type="button" onClick={() => setCatOpen(true)}
                        className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-bold"
                    >카테고리 관리</button>
                    <button
                        type="button" onClick={() => setAuthorOpen(true)}
                        className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-bold"
                    >저자 관리</button>
                    <select className={selectCls} value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="">전체 종류</option>
                        {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">전체 상태</option>
                        <option value="draft">초안</option>
                        <option value="published">발행됨</option>
                    </select>
                    <input
                        className={selectCls}
                        placeholder="제목·주소 검색"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">{error}</div>
            )}

            {!loading && (
                <div className="grid grid-cols-3 gap-3 mb-5">
                    {[
                        ['전체', total, 'text-gray-800'],
                        ['발행 가능', ready, 'text-emerald-600'],
                        ['발행됨', published, 'text-primary-600'],
                    ].map(([label, n, cls]) => (
                        <div key={label} className="bg-white rounded-xl border border-gray-200 px-4 py-3">
                            <div className="text-xs font-bold text-gray-500">{label}</div>
                            <div className={`text-2xl font-extrabold ${cls}`}>{n}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs">
                            <tr>
                                <th className="px-4 py-3 font-bold w-24">종류</th>
                                <th className="px-4 py-3 font-bold">제목</th>
                                <th className="px-4 py-3 font-bold w-56">주소</th>
                                <th className="px-4 py-3 font-bold w-24">상태</th>
                                <th className="px-4 py-3 font-bold w-40">남은 조건</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400 animate-pulse">불러오는 중…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">해당하는 콘텐츠가 없습니다.</td></tr>
                            ) : rows.map((r) => (
                                <tr
                                    key={r.id}
                                    onClick={() => navigate(`/admin/contents/${r.id}`)}
                                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                >
                                    <td className="px-4 py-3">
                                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] font-bold">
                                            {TYPE_LABEL[r.type] ?? r.type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-semibold text-gray-900">
                                        {r.title}
                                        {r.categoryLabel && (
                                            <span className="ml-2 text-[11px] font-normal text-gray-400">{r.categoryLabel}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-[11px] text-gray-500 break-all">
                                        {/^(legacy|draft)-/.test(r.slug)
                                            ? <span className="text-amber-700">{r.slug} (임시)</span>
                                            : r.slug}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                                            r.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {STATUS_LABEL[r.status] ?? r.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.fail > 0 ? (
                                            <span className="text-red-600 font-bold text-xs">필수 {r.fail}건</span>
                                        ) : (
                                            <span className="text-emerald-600 font-bold text-xs">발행 가능</span>
                                        )}
                                        {r.warn > 0 && (
                                            <span className="ml-2 text-amber-600 text-xs">경고 {r.warn}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="mt-4 text-xs text-gray-500 leading-relaxed">
                이 목록은 <b className="text-gray-700">cms.contents</b> 입니다. 기존 솔루션·사례·제품 화면이 쓰는
                레거시 테이블과 별개이고, 공개 사이트는 아직 레거시 쪽을 읽습니다 — 여기서 발행해도 지금 사이트 표시는 바뀌지 않습니다.
            </p>

            {/* 노트 카테고리는 4종이 시드돼 있고 추가·이름 변경이 가능하다. */}
            <ContentCategoryManager
                isOpen={catOpen}
                onClose={() => { setCatOpen(false); load(); }}
                contentType={type || 'note'}
                title={`${TYPE_LABEL[type] ?? '기술노트'} 카테고리 관리`}
            />
            <AuthorManager isOpen={authorOpen} onClose={() => setAuthorOpen(false)} onChanged={load} />
        </AdminLayout>
    );
};

export default ContentReview;
